'use client'

import React, { useState } from 'react'
import type { Post } from '@/payload-types'
import { Socials, type SocialLinks, type SupportedSocials, type SocialProps } from '../Socials'
import { ArrowLeftIcon } from './ArrowLeftIcon'
import { ArrowRightIcon } from './ArrowRightIcon'

import { useSiteSettings } from '@/providers/SiteSettings'

interface ShareLinkOptions {
    slug: string
    title: string
    description?: string
    domain: string
}

function getUTMParameters(social: SupportedSocials) {
    return `utm_medium=sharefromsite&utm_source=quartz_${social}`
}

function createShareLinks({ slug, title, description = '', domain }: ShareLinkOptions): SocialLinks {
    const url = `https://${domain}/${slug}?`
    const encodedUrl = encodeURIComponent(url)
    const encodedTitle = encodeURIComponent(title)
    const encodedDescription = encodeURIComponent(description)

    return {
        // Main platforms
        twitter: `https://twitter.com/intent/tweet?url=${encodedUrl + encodeURIComponent(getUTMParameters('twitter'))}&text=${encodedTitle}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl + encodeURIComponent(getUTMParameters('facebook'))}`,
        reddit: `https://reddit.com/submit?url=${encodedUrl + encodeURIComponent(getUTMParameters('reddit'))}&title=${encodedTitle}`,
        email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%20${encodedUrl + encodeURIComponent(getUTMParameters('email'))}`,
        link: `${url}${getUTMParameters('link')}`,

        // Newer platforms
        // bluesky: `https://bsky.app/intent/compose?text=${encodedTitle}%20${encodedUrl}`,
        // mastodon: `web+mastodon://share?text=${encodedTitle}%20${encodedUrl}`,
        // threads: `https://threads.net/intent/post?text=${encodedTitle}%20${encodedUrl}`,
    }
}

interface Props extends Omit<SocialProps, 'links' | 'useShareToSocial'> {
    post: Pick<Post, 'slug' | 'title'>
    useHideSocialButton?: boolean
}

export const SocialShare: React.FC<Props> = ({ post, useHideSocialButton, ...rest }) => {
    const site = useSiteSettings()
    const shareLinks = createShareLinks({ slug: `${post.slug}`, title: post.title, domain: site.siteDomain || '' })
    const [showSocial, setShowSocial] = useState<boolean>(false)

    const handleClick = () => {
        setShowSocial(!showSocial)
    }
    return (
        <>
            {useHideSocialButton && (
                <button className='cursor-pointer' onClick={handleClick} aria-label={(showSocial ? 'Show' : 'Hide') + ' share options'}>
                    {showSocial ? <ArrowLeftIcon /> : <ArrowRightIcon />}
                </button>
            )}
            {(!useHideSocialButton || showSocial) && <Socials {...rest} links={shareLinks} useShareToSocial />}
        </>
    )
}
